import { Box, Button, CardMedia, Dialog, FormControl, Grid, IconButton, InputAdornment, Badge, Tooltip, Typography, Theme, TextField, useTheme, TablePagination, } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { APPLICATION_LISTING_STATUS, APPLICATION_STATUS, POA_APPLICATION_STATUS, USER_ROLES, applicantStatusMasters, MPOWER_ACCOUNT_TYPE_MASTER, APPLICATION_STATUS_AMCAPPROVER_individual, COMMENT_ROLES } from '../../utils/constant';
import {
  checkIfApplicationIsNonIndividual,
  getFirstHolderName,
  isModuleAllowed,
  maskInfo,
} from '../../utils/utilityFunctions';
import { ApplicationProps, } from '../../redux-store/types/api-types';
import { useDispatch, useSelector } from 'react-redux';
import { getAllApplications } from '../../redux-store/actions/application';
import { GetAllApplicantionsResponseBody } from '../../redux-store/types/application';
import { CommonLayout } from '../commonComponents';
import { ColumnType, DataTable } from '../DataTable';
import { useDebounce } from '../../utils/useDebounce';
import { RootStateType } from '../../redux-store/reducers';
import CloseIcon from '@mui/icons-material/Close';
import { FilterDropdown, initialFilters } from '../investors/filters';
import { Distributor, getDistributorsListForFilters } from '../../redux-store/actions';
import { ApplicationFilterType } from '../../redux-store/types/api-types';
import { SearchIcon } from '../customSVGs';
import { FilterIcon, InfoIconOutlined } from '../../assets/SvgIcons';
import dayjs from '../../utils/dayjs';

const ApplicationComplianceStatusBasedOnRole: { [key: string]: { [key: string]: string } } = {
  [USER_ROLES.POAAPPROVER]: {
    ...POA_APPLICATION_STATUS,
    sent_to_poa_approver: 'Pending',
  },
  [USER_ROLES.AMCAPPROVER]: {
    ...POA_APPLICATION_STATUS,
    sent_to_amc_approver: 'Pending',
  },
  [USER_ROLES.OPE]: {
    ...POA_APPLICATION_STATUS,
    sent_to_compliance: 'Pending',
  },
  [USER_ROLES.OPM]: {
    ...POA_APPLICATION_STATUS,
    sent_to_compliance: 'Pending',
  },
};

const getApplicationComplianceStatusBasedOnRole = (role: string): { [key: string]: string } => {
  return ApplicationComplianceStatusBasedOnRole[role] ?? POA_APPLICATION_STATUS;
};

const IndividualComplianceApplicationStatusBasedOnRole: { [key: string]: { [key: string]: string } } =
{
  [USER_ROLES.AMCAPPROVER]: {
    ...APPLICATION_STATUS_AMCAPPROVER_individual,
    sent_to_amc_approver: 'Pending',
  },
  [USER_ROLES.OPE]: {
    ...APPLICATION_STATUS_AMCAPPROVER_individual,
    sent_to_compliance: 'Pending',
  },
  [USER_ROLES.OPM]: {
    ...APPLICATION_STATUS_AMCAPPROVER_individual,
    sent_to_compliance: 'Pending',
  },
};

const getIndividualApplicationComplianceStatusBasedOnRole = (role: string): { [key: string]: string } => {
  return IndividualComplianceApplicationStatusBasedOnRole[role] ?? APPLICATION_STATUS_AMCAPPROVER_individual;
};

const statusMap: { [key: string]: string } = {
  pending: 'BSE Client Activation Pending',
  active: 'BSE Client Activated',
  failed: 'BSE Client Activation Failed',
}

const getBseClientActivationStatusMsg = (value: string): string => {
  if (!['pending', 'active', 'failed'].includes(value)) {
    return value
  } else {
    return statusMap[value]
  }
}

const getTableHeader = (role: string): ColumnType[] => {
  return [
    {
      header: 'Investor Name',
      valueGetter: (row: ApplicationProps) =>
        row?.onboardingType?.toLowerCase() === 'minor'
          ? !(row?.applicants[0]?.minorFirstName) ? "N/A"
            :
            `${row?.applicants[0]?.minorFirstName ?? ""} ${row?.applicants[0]?.minorLastName ?? ""}`
          : (getFirstHolderName(row?.applicants)) || 'N/A',

    },
    {
      header: 'Application ID',
      label: 'applicationNumber',
    },
    {
      header: 'Client Code',
      valueGetter: (row: ApplicationProps) =>
        row?.clientCode || 'N/A',

    },
    {
      header: 'Account Type',
      valueGetter: (row: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {MPOWER_ACCOUNT_TYPE_MASTER[row?.accountType] ?? 'N/A'}
        </Typography>
      ),
    },
    {
      header: 'Mode of Holding / Status',
      renderCell: (row: ApplicationProps) => {
        return (
          <Typography
            variant='subtitle_500'
            sx={{ textTransform: 'capitalize', }}
          >
            {row.modeOfHolding || 'Single'} / {
              row?.applicants?.[0]?.status ?
                applicantStatusMasters[row.applicants[0].status] ?
                  applicantStatusMasters[row.applicants[0].status] : row.applicants[0].status
                : "N/A"
            }
          </Typography>
        );
      },
    },
    {
      header: 'Investor PAN',
      valueGetter: ({ applicants = [] }: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {applicants[0]?.panNo || 'N/A'}
        </Typography>
      ),
    },
    {
      header: 'Investor Email ID',
      valueGetter: ({ applicants = [] }: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {maskInfo(applicants[0]?.email ?? '') || 'N/A'}
        </Typography>
      ),
    },
    {
      header: 'RM Name',
      valueGetter: (row: ApplicationProps) => (
        <Typography variant='subtitle_500'>
          {row?.rm?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      header: 'Approval Status',
      renderCell: (row: ApplicationProps) => {
        const applicationStatus = row.hasPOA
          ? getApplicationComplianceStatusBasedOnRole(role)?.[row.status]
          : checkIfApplicationIsNonIndividual(row)
            ? getApplicationComplianceStatusBasedOnRole(role)?.[row.status]
            : (!checkIfApplicationIsNonIndividual(row))
              ? getIndividualApplicationComplianceStatusBasedOnRole(role)?.[row.status]
              : APPLICATION_LISTING_STATUS[row.status];

        const sentBackByRoleLabel = `${row?.sentBackByRole ?? ""}`.toLowerCase() === "amcapprover"
          ? "By Checker"
          : "By Compliance";

        const rejectedByRoleLabel = `${row?.rejectedByRole ?? ""}`.toLowerCase() === "amcapprover"
          ? "By Checker"
          : "By Compliance";

        return (
          <Typography
            className={applicationStatus}
            sx={(theme: Theme) => ({
              textDecoration: 'none',
              ...theme.typography.subtitle_500,
              color: '#B78813',
              '&.Draft': {
                color: 'rgba(51, 127, 201, 0.7)',
              },
              '&.Completed': {
                fontWeight: 600,
                color: 'rgba(35, 133, 63, 0.7)',
              },
              '&.Rejected': {
                color: '#BD2932 !important',
                fontWeight: 500,
              },
            })}
            component={RouterLink}
            to={`/compliance-application-details/${row.id}`}
          >
            {applicationStatus} {row.applicationSentBack ? ` (Sent Back)${row?.sentBackByRole ? ` (${sentBackByRoleLabel})` : ''}` : ''} {((applicationStatus === "Rejected") && row?.rejectedByRole) ? ` (${rejectedByRoleLabel})` : ''}
          </Typography>
        );
      },
    },
    {
      header: 'BSE STAR STATUS',
      valueGetter: (row: ApplicationProps) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            variant='subtitle_500'
          >
            {row?.isBSEEnabled
              ? row?.bseClientActivationStatus
                ? getBseClientActivationStatusMsg(row?.bseClientActivationStatus)
                : 'N/A'
              : 'BSE Disabled'
            }
          </Typography>
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  margin: "0px !important",
                  backgroundColor: "transparent"
                }
              }
            }}
            title={
              <Box sx={{
                borderRadius: '10px',
                backgroundColor: '#0F131F',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '10px'
              }}>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    STATUS
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseStarServiceResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    CLIENT CREATION RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseStarResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    FATCA RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseFatcaResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    CHEQUE UPLOAD RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.cancelChequeUploadResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    AOF IMAGE RESPONSE
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.bseAofImageResponse || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='subtitle_500' sx={(theme: Theme) => ({ fontWeight: '600', marginBottom: '5px', color: theme.palette.primary[500] })}>
                    MPOWER STATUS
                  </Typography>
                  <Typography variant='subtitle_600' sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    {row?.mpowerResponse || '-'}
                  </Typography>
                </Box>
              </Box>
            }
          >
            <IconButton>
              <InfoIconOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];
};

function ComplianceApprovalListView({
  tableHeaders,
}: {
  tableHeaders: ColumnType[];
}): JSX.Element {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [applicationsList, setApplicationsList] = useState<ApplicationProps[]>([]);
  const { role = '', permissions = [] } = useSelector((store: RootStateType) => store.auth);
  const { palette } = useTheme();
  const {
    page,
    search: storeSearch,
    status: storeStatus,
    applicationType: storeApplicationType,
    modeOfHolding: storeModeOfHolding,
    distributorId: storeDistributorId,
    diy: storeDIY,
    accountType: storeAccountType,
    onboardingType: storeOnboardingType,
  } = useSelector((store: RootStateType) => store.paramsObj);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<boolean>(false);
  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const defaultStatus = !storeStatus ? 'sent_to_compliance' : storeStatus;
  const [filters, setFilters] = useState<ApplicationFilterType>(
    initialFilters(role, defaultStatus, storeApplicationType, storeModeOfHolding, storeDistributorId, storeDIY, storeAccountType, storeOnboardingType)
  );
  const { applicationType, distributorId, status, modeOfHolding, diy, accountType, onboardingType } = filters;
  // const [noOfPages, setNoPages] = useState(1);
  // const [currentPage, setPage] = useState(page);
  const [search, setSearch] = useState(storeSearch);
  const [noOfPages, setNoPages] = useState(1);
  const [currentPage, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0)
  const [limit, setLimit] = useState(10);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const debounceSearchString = useDebounce(search, 500);
  const [distributorLoading, setDistributorLoading] = useState(false);
  const [totalFilters, setTotalFilters] = useState<number>(!storeStatus ? 1 : 0);

  useEffect(() => {
    let componentIsAlive = true;
    setLoading(true);
    setApplicationsList([]);
    (async function () {
      try {
        const response = (await dispatch(
          getAllApplications({
            page: currentPage,
            search,
            applicationType: applicationType.toString(),
            distributorId: distributorId.toString(),
            status: status.toString(),
            modeOfHolding: modeOfHolding.toString(),
            onboardingType: onboardingType?.toString(),
            diy: diy?.toString(),
            accountType: accountType?.toString(),
            ...(isModuleAllowed({ value: 'complianceChecker', compareArr: permissions, objKey: 'permissonCode' }) ? { isCompliance: true } : {}),
          })
        )) as unknown as GetAllApplicantionsResponseBody;
        const { applications, pages, total, limit } = response || {};
        if (!componentIsAlive) {
          return;
        }
        setApplicationsList(applications);
        setNoPages(pages);
        setTotalCount(total)
        setLimit(limit);
        setLoading(false);
        if (response) {
          const totalAppliedFilters = (applicationType?.length || 0) + (modeOfHolding?.length || 0) + (status?.length || 0) + (distributorId?.length || 0) + (diy?.length || 0) + (accountType?.length || 0) + (onboardingType?.length || 0);
          setTotalFilters(totalAppliedFilters);
        }
      } catch (e) {
        console.error((e as Error).message);
        setLoading(false);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, [currentPage, debounceSearchString, filters]);

  // useEffect(() => {
  //   setPage(page);
  // }, [debounceSearchString]);

  const handleFiltersAnchorEl = async () => {
    try {
      setFiltersAnchorEl(true);
      setDistributorLoading(true);
      const distributorList = (await dispatch(
        getDistributorsListForFilters()
      )) as unknown as Distributor[];
      setDistributors(distributorList);
      setDistributorLoading(false);
    } catch (e) {
      console.error((e as Error).message);
      setDistributorLoading(false);
    }
  };

  const handleFiltersAnchorElClose = () => {
    setFiltersAnchorEl(false);
  };
  const filterOptions = (totalFilters: number, setTotalFilters: React.Dispatch<React.SetStateAction<number>>): JSX.Element => {
    const badgeCount = ['applicationType', 'modeOfHolding', 'status', 'distributorId', 'diy', 'accountType', 'onboardingType'].filter(
      (filterKey) => (filters[filterKey] as string[]).length
    ).length;
    return (
      <>
        {/* <IconButton
            sx={{ p: 0.5 }}
            id="filter-icon-button"
            aria-controls="filter-menu"
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleFiltersAnchorEl}>
            <Badge badgeContent={badgeCount} color="info">
              <CardMedia component="img" image="/images/filter-icon.svg" alt="Filter Icon" />
            </Badge>
          </IconButton>{' '} */}
        <Button
          variant="text"
          startIcon={
            <Badge
              showZero
              badgeContent={totalFilters}
              sx={(theme: Theme) => ({
                '& .MuiBadge-badge': {
                  right: 0,
                  top: -3,
                  backgroundColor: theme.palette.primary[500],
                  color: theme.palette.common.white,
                  ...theme.typography.subtitle_600
                },
              })}
            >
              <FilterIcon style={{ opacity: isLoading ? 0.5 : 1 }} />
            </Badge>
          }
          sx={(theme: Theme) => ({
            color: theme.palette.text.primary,
            padding: "5px",
            ...theme.typography.body_500,
            // "&:hover": {
            //   backgroundColor: theme.palette.grey[50]
            // }
          })}
          TouchRippleProps={{
            style: {
              color: palette.grey[300]
            }
          }}
          onClick={handleFiltersAnchorEl}
          disabled={isLoading}
        >
          Filters
        </Button>
        {filtersAnchorEl && (
          <FilterDropdown
            filters={filters}
            onFiltersChange={(value) => {
              setPage(1);
              setFilters(value);
            }}
            anchorEl={filtersAnchorEl}
            handleClose={handleFiltersAnchorElClose}
            distributors={distributors}
            loader={distributorLoading}
            setTotalFilters={setTotalFilters}
          />
        )}
      </>
    );
  };

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    // <Box
    //   sx={{
    //     ml: { xs: 0 },
    //     p: { xs: 2, md: '50px' },
    //     borderRadius: '50px 0 0 0',
    //     bgcolor: { xs: '', md: 'rgba(238, 244, 251, 0.5)' },
    //     // minHeight: 'calc(100vh - 108px)',
    //   }}>
    <Box>
      <Box sx={{ marginBottom: '20px' }}>
        <Typography sx={{ color: 'text.primary' }} variant='h1'>
          Awaiting Compliance Approvals
        </Typography>
      </Box>
      <Box style={{ background: palette.background.paper, borderRadius: '25px', overflow: 'hidden' }}>
        <Grid
          container
          sx={{
            p: '20px 30px',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            bgcolor: 'common.white',
            mb: 1.5,
          }}
          rowGap={1.5}>
          <Grid item container sx={{ width: 'unset', alignItems: 'flex-end', gap: 2 }}>
            <Grid item>
              <FormControl variant="standard" sx={{ display: 'block' }}>
                {/* <InputLabel
                    htmlFor="input-with-icon-adornment"
                    sx={{ '&.Mui-focused': { color: 'text.primary' } }}>
                    Search Application
                  </InputLabel>
                  <Input
                    id="input-with-icon-adornment"
                    value={search}
                    onChange={({ target: { value } }) => {
                      setPage(1);
                      setSearch(value);
                    }}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton>
                          <SearchSharpIcon sx={{ color: 'primary.main' }} />
                        </IconButton>
                      </InputAdornment>
                    }
                  /> */}
                <TextField
                  id="outlined-multiline-flexible"
                  value={search}
                  placeholder={`Search Application`}

                  onChange={({ target: { value } }) => {
                    setPage(1);
                    setSearch(value);
                  }}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    style: {
                      borderRadius: '5px',
                      border: `1px solid ${palette.text.secondary}`,
                      height: '40px',
                      width: '100%',
                      maxWidth: '255px',
                      fontSize: '16px',
                    },
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid item sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {/* <FilterMenu /> */}
            {filterOptions(totalFilters, setTotalFilters)}
          </Grid>
        </Grid>
        <DataTable
          isLoading={isLoading}
          tableHeader={tableHeaders}
          tableData={applicationsList}
          // noOfPages={noOfPages}
          // currentPage={currentPage}
          // paginationAction={handlePageChange}
          // rowCustomStyles={{
          //   '&:last-child td, &:last-child th': { border: 0 },
          //   '.MuiTableCell-body ': { color: '#686e72' },
          //   background: 'transparent',
          // }}
          rowCustomStyles={{
            '&:last-child td, &:last-child th': { border: 0 },
            '.MuiTableCell-body ': { color: palette.text.primary, fontWeight: 500 },
            background: 'transparent',
          }}
          boxStyles={{ minWidth: 100 }}
        />
        <TablePagination
          style={{
            color: palette.text.primary[500],
            fontSize: 16,
            fontWeight: 500,
            padding: '20px 35px 35px 35px',
            // paddingLeft: '50px',
            // paddingBottom: '48px'
          }}
          rowsPerPageOptions={[]}
          labelRowsPerPage=""
          component="div"
          count={totalCount}
          rowsPerPage={limit}
          page={currentPage - 1}
          onPageChange={(event, num) => setPage(num + 1)}
          // onRowsPerPageChange={(rowsPerPage) => console.log('rowsPerPage', rowsPerPage.target.value)}
          sx={{
            color: palette.text.primary[500],
            '& .MuiSvgIcon-root': {
              color: palette.customColors.infoCardTitle,
            },
            '& .MuiSelect-icon': {
              color: palette.customColors.infoCardTitle,
            },
            '& svg': {
              color: palette.customColors.infoCardTitle,
            }
          }} />
      </Box>
    </Box>
  );
}

export default function ComplianceApprovalList(): JSX.Element {

  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [rejectedApplication, setRejectedApplication] = useState<ApplicationProps>();

  const handleRejectionDialogClose = () => {
    setRejectionDialog(false);
  };

  const rejectionCell = {
    header: '',
    renderCell: (row: ApplicationProps) => {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
            }}>
            {APPLICATION_STATUS[row.status] === 'Rejected' && (
              <IconButton>
                <CardMedia
                  component="img"
                  src="/images/comment.svg"
                  alt="comment"
                  sx={{ width: '20px' }}
                  onClick={() => {
                    setRejectedApplication(row);
                    setRejectionDialog(true);
                  }}
                />
              </IconButton>
            )}
          </Box>
        </>
      );
    },
  };

  return (
    <CommonLayout>
      <>
        <ComplianceApprovalListView
          tableHeaders={[
            ...getTableHeader(role),
            rejectionCell
          ]} />
        <Dialog
          onClose={handleRejectionDialogClose}
          open={rejectionDialog}
          sx={{
            '.MuiPaper-root ': {
              width: { xs: '70%', sm: '70%' },
            },
          }}>
          <IconButton
            onClick={handleRejectionDialogClose}
            sx={{ alignSelf: 'flex-end', mt: 2, mr: 2 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
          {rejectedApplication && (
            <Box
              sx={{
                width: { xs: '80%', sm: '80%' },
                mx: 'auto',
                '.MuiTypography-root': {
                  fontWeight: 500,
                },
                mt: 1,
                mb: 5,
              }}>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '18px',
                  letterSpacing: '0.4px',
                  ml: { sm: 6.7 },
                  mt: 1,
                }}>
                Application Id:{' '}
                <Typography
                  component="span"
                  sx={{
                    color: '#337FC9',
                    fontSize: '16px',
                    letterSpacing: '0.4px',
                    ml: 0.5,
                  }}>
                  {rejectedApplication.applicationNumber}
                </Typography>
              </Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '18px',
                  letterSpacing: '0.4px',
                  ml: { sm: 1.5 },
                  mt: 1,
                }}>
                Application Status:{' '}
                <Typography
                  component="span"
                  sx={{
                    color: '#BD2932',
                    fontSize: '16px',
                    letterSpacing: '0.4px',
                    ml: 0.5,
                  }}>
                  {POA_APPLICATION_STATUS[rejectedApplication.status]}
                </Typography>
              </Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '18px',
                  letterSpacing: '0.4px',
                  ml: { sm: 9 },
                  mt: 1,
                }}>
                Rejected on:{' '}
                <Typography
                  component="span"
                  sx={{
                    color: '#337FC9',
                    fontSize: '16px',
                    letterSpacing: '0.4px',
                    ml: 0.5,
                  }}>
                  {dayjs(rejectedApplication?.rejectedAt).format('DD MMM YY h:mm A')}
                </Typography>
              </Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '18px',
                  letterSpacing: '0.4px',
                  ml: { sm: 9.5 },
                  mt: 1,
                }}>
                Rejected by:{' '}
                <Typography
                  component="span"
                  sx={{
                    color: '#337FC9',
                    fontSize: '16px',
                    letterSpacing: '0.4px',
                    ml: 0.5,
                  }}>
                  {rejectedApplication?.rejectedBy}
                </Typography>
              </Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontSize: '18px',
                  letterSpacing: '0.4px',
                  mt: 1,
                }}>
                Reason for rejection:{' '}
                <Typography
                  component="span"
                  sx={{
                    color: '#337FC9',
                    fontSize: '16px',
                    letterSpacing: '0.4px',
                    ml: 0.5,
                  }}>
                  {rejectedApplication.rejectedReason}
                </Typography>
              </Typography>
              <Button
                variant="contained"
                sx={{ ml: '32%', lineHeight: 1.1, width: '30%', mt: 3 }}
                onClick={handleRejectionDialogClose}>
                Done
              </Button>
            </Box >
          )
          }
        </Dialog >
      </>
    </CommonLayout>
  );
}
